/**
 * Pool Reports Download Page
 *
 * Allows users to download Excel and XML versions of pool reports
 * UI matches Angular implementation exactly
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, FileCode } from 'lucide-react';
import { Button, Alert, Modal, Skeleton } from '@/components/ui';
import { PoolReportTableCard } from '@/components/poolReports/PoolReportTableCard';
import { useDownloadables } from '@/hooks';
import { PoolReportsService } from '@/services/poolReportsService';

export function PoolReportsDownloadPage() {
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get('fileId');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [downloadType, setDownloadType] = useState<'xml' | 'excel' | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch downloadables using React Query hook
  const { data: downloadable, isLoading } = useDownloadables(
    fileId ? parseInt(fileId, 10) : 0,
    !!fileId
  );

  const handleDownloadClick = (type: 'xml' | 'excel') => {
    setError(null);
    setDownloadType(type);
    setIsConfirmOpen(true);
  };

  const handleDownloadConfirm = async () => {
    if (!fileId || !downloadType) return;

    setDownloading(true);
    setError(null);

    try {
      const { blob, fileName } = await PoolReportsService.downloadFile(
        parseInt(fileId, 10),
        downloadType
      );

      // Create download link and trigger download (matching Angular logic)
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      document.body.appendChild(link);
      link.href = url;
      link.download = fileName;
      link.setAttribute('style', 'display: none');
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      link.remove();

      setIsConfirmOpen(false);
      setDownloadType(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download file');
    } finally {
      setDownloading(false);
    }
  };

  const handleCloseConfirm = () => {
    if (!downloading) {
      setIsConfirmOpen(false);
      setDownloadType(null);
      setError(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Error Alert */}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <PoolReportTableCard
          eyebrow="Download"
          title="Report files"
          description="Preparing download options…"
        >
          <div className="space-y-4 p-4 sm:p-5">
            <div className="space-y-2 rounded-lg border border-gray-200/80 bg-gray-50/50 p-3 dark:border-dark-600 dark:bg-dark-900/30">
              <Skeleton className="h-4 w-56" />
              <Skeleton className="h-10 w-full max-w-xs" />
            </div>
            <div className="space-y-2 rounded-lg border border-gray-200/80 bg-gray-50/50 p-3 dark:border-dark-600 dark:bg-dark-900/30">
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-10 w-full max-w-xs" />
            </div>
          </div>
        </PoolReportTableCard>
      )}

      {/* Download Options */}
      {!isLoading && downloadable && (
        <PoolReportTableCard
          eyebrow="Download"
          title="Report files"
          description="Choose a format. You’ll confirm before the file is saved."
        >
          <div className="space-y-3 p-4 sm:space-y-4 sm:p-5">
            <div className="rounded-lg border border-l-4 border-gray-200/90 border-l-primary-600 bg-gradient-to-r from-gray-50/90 to-white p-3 shadow-sm dark:border-dark-600 dark:border-l-primary-500 dark:from-dark-900/50 dark:to-dark-800/80 sm:p-4">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Vessel details (XML)
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Net allocations and pool points per vessel.
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleDownloadClick('xml')}
                leftIcon={<FileCode className="h-4 w-4" />}
                className="mt-3 rounded-full px-5 shadow-md"
              >
                Download vessel details
              </Button>
            </div>

            {downloadable.ExcelFileName && (
              <div className="rounded-lg border border-l-4 border-gray-200/90 border-l-[#4a5682] bg-gradient-to-r from-slate-50/90 to-white p-3 shadow-sm dark:border-dark-600 dark:border-l-sky-500/80 dark:from-dark-900/50 dark:to-dark-800/80 sm:p-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Excel workbook</p>
                <p className="mt-1 break-all text-xs text-gray-500 dark:text-gray-400">
                  {downloadable.ExcelFileName}
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleDownloadClick('excel')}
                  leftIcon={<Download className="h-4 w-4" />}
                  className="mt-3 rounded-full px-5 shadow-md"
                >
                  Download Excel report
                </Button>
              </div>
            )}
          </div>
        </PoolReportTableCard>
      )}

      {/* No Files Available */}
      {!isLoading && !downloadable && !error && (
        <PoolReportTableCard
          eyebrow="Download"
          title="Report files"
          description="Nothing to download for this report."
        >
          <div className="flex flex-col items-center px-4 py-10 text-center sm:py-12">
            <Download className="h-10 w-10 text-gray-400" />
            <h3 className="mt-3 text-base font-semibold text-gray-900 dark:text-white">
              No files available
            </h3>
            <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
              No downloadable files are available for this report.
            </p>
          </div>
        </PoolReportTableCard>
      )}

      {/* Download Confirmation Modal - Angular Style */}
      <Modal isOpen={isConfirmOpen} onClose={handleCloseConfirm} title="File Download" size="sm">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/20">
              <Download className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <p className="text-gray-900 dark:text-white">
              <strong>Do you want to download this file?</strong>
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={handleCloseConfirm} disabled={downloading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleDownloadConfirm}
              disabled={downloading}
              loading={downloading}
            >
              Download
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
